import * as React from 'react'

import getConfig from 'next/config'

import { Product as SdProduct } from '@grandvisionhq/analytics'
import { Products as ProductsApi } from '@grandvisionhq/api-client'
import {
  CmsComponentSlot,
  CmsHeadTags,
  CmsProductBreadcrumbs,
  useBloomreachPageRoutes,
  useBloomreachPdpContent,
  useBloomreachProductRoutes,
} from '@grandvisionhq/bloomreach'
import { MiniCartModal, Upsell } from '@grandvisionhq/carts-v2'
import { CommerceToolsApi } from '@grandvisionhq/commercetools'
import { VtoConfigProvider } from '@grandvisionhq/fun-products'
import { NextPage, withErrorPage } from '@grandvisionhq/next'
import type { SspConfig } from '@grandvisionhq/products'
import {
  getProductSeoInfo,
  Helpers as ProductHelpers,
  parseProductDetailQueryParams,
  ProductDetail,
  ProductDetailProvider,
  ProductFrameMeasurements,
} from '@grandvisionhq/products'
import { ReviewDisplay, ReviewsProvider } from '@grandvisionhq/reviews'
import { CrossSellProducts } from '@grandvisionhq/search'
import { DeliveryIndicatorProvider } from '@grandvisionhq/shipping'
import { useFeatureToggles, useIntl } from '@grandvisionhq/state'
import { compose, getAttrValue, isString } from '@grandvisionhq/utils'

import { withCms, withLayout } from '../../cms'
import { defaultErrorProps, ErrorProps } from '../../components/error-props'
import { settings as cartSettings, withCart } from '../../components/hocs/with-cart'
import { getDeliveryIndicatorConfig } from '../../configuration'
import { getAlgoliaCrossSellConfig } from '../../util/algolia'
import { getAllowedExtras } from '../../util/get-allowed-extras'

import '@grandvisionhq/reviews/dist/index.css'

const { publicRuntimeConfig } = getConfig()
const endpoints = publicRuntimeConfig?.grandvision?.endpoints || {}
const staticsUrl = publicRuntimeConfig?.grandvision?.staticsUrl || ''
const selfUrl = publicRuntimeConfig?.selfUrl || ''
const vtoConfig = publicRuntimeConfig?.ditto || {}
const { apiKey: powerReviewsApiKey, groupId, opcoId } = publicRuntimeConfig?.powerReviews || {}

const featureToggles: Record<string, boolean> = Object.entries({
  isPrescriptionInfoEnabled: () => false,
}).reduce((result, [key, fn]) => ({ ...result, [key]: fn() }), {})

const deliveryIndicatorConfig = getDeliveryIndicatorConfig()

type ReviewProviderProps = React.ComponentProps<typeof ReviewsProvider>

type InitialProps = {
  sku: string
  product?: CommerceToolsApi.ProductProjection
  queryParams: Record<string, string | string[]>
  sspExtraProducts?: CommerceToolsApi.ProductProjection[]
}

type Props = InitialProps

export const ProductDetailPage: NextPage<Props, InitialProps | ErrorProps> = ({
  sku,
  product,
  sspExtraProducts,
  queryParams,
}) => {
  const { locale } = useIntl()
  const content = useBloomreachPdpContent()
  const { routes } = useBloomreachPageRoutes()
  const [, { formatProductUrl }] = useBloomreachProductRoutes()
  const { isBynderImagesEnabled, isCrossSellEnabled, isReviewsEnabled } = useFeatureToggles()

  /**
   * Product data, seo, cross-sell configurations
   */
  const productType = product?.productType?.obj?.name as CommerceToolsApi.ProductTypeName
  const productUrl = selfUrl + formatProductUrl({ slug: product?.slug ?? {}, productType, sku })
  const brandKey = String(getAttrValue(product?.masterVariant ?? {}, 'brandKey'))
  const seoInfo = getProductSeoInfo(staticsUrl, locale, product, undefined, isBynderImagesEnabled)
  const crossSellConfig = React.useMemo(() => getAlgoliaCrossSellConfig(productType), [productType])

  /**
   * Product type checks
   */
  const isLenses = React.useMemo(() => CommerceToolsApi.isLenses(productType), [productType])
  const isSunglasses = React.useMemo(
    () => CommerceToolsApi.isSunglasses(productType),
    [productType]
  )
  const isFrames = React.useMemo(() => CommerceToolsApi.isFrames(productType), [productType])

  /**
   * PowerReviews setup
   */
  const reviewsInitialId = isLenses ? brandKey : product?.masterVariant.sku
  const reviewsInitialSku = isLenses ? undefined : sku // SKU is not required for CL
  const reviewsProviderProps = React.useMemo<ReviewProviderProps>(
    () => ({
      enabled: isReviewsEnabled && !!product?.reviewEnabled,
      config: {
        groupId,
        opcoId,
        apiKey: powerReviewsApiKey,
        writeReviewPath: routes['write-review'],
      },
    }),
    // other fields are static during runtime
    [product, routes]
  )

  // We need to map appointment-book to book-appointment, since it's hardcoded in core.
  const adjustedRoutes = React.useMemo(
    () => ({
      ...routes,
      'book-appointment': routes['appointment-book'],
    }),
    [routes]
  )

  if (!product) {
    console.error(`Failed to render ProductDetailPage because of invalid product data: ${product}`)
    return null
  }

  /**
   * Cart v2 upsell setup (optional)
   */
  const productCatKey = getAttrValue(product.masterVariant, 'recommendedProductCatKey')
  const recommendedCategoryKey = isString(productCatKey) ? productCatKey : undefined
  const params = parseProductDetailQueryParams(queryParams)

  /**
   * SSP
   */
  const sspConfig: SspConfig | undefined = !!sspExtraProducts?.length
    ? { sspExtraProducts, sspResolver: getAllowedExtras, initialGlassesKey: 'frameOnly' }
    : undefined

  return (
    <>
      <CmsHeadTags
        title={seoInfo.title}
        description={seoInfo.description}
        ogImage={seoInfo.imageUrl}
        ogUrl={productUrl}
      >
        {isReviewsEnabled && <link rel="preconnect" href="https://ui.powerreviews.com" />}
      </CmsHeadTags>

      <CmsComponentSlot path="main/top-container" />
      <DeliveryIndicatorProvider config={deliveryIndicatorConfig}>
        <ProductDetailProvider
          product={product}
          sku={sku}
          routes={adjustedRoutes}
          productType={productType}
          locale={locale}
          staticsUrl={staticsUrl}
          {...params}
        >
          <CmsProductBreadcrumbs product={product} sku={sku} />
          <ReviewsProvider {...reviewsProviderProps}>
            <VtoConfigProvider config={vtoConfig}>
              <ProductDetail
                brandPromotions={content.brandPromotions}
                colorFeatureIcons="orange"
                disclaimer={content.disclaimer}
                formatProductUrl={formatProductUrl}
                maxOrderableQuantity={cartSettings.maximumQuantity}
                productType={productType}
                productTypeUsps={content.usps}
                vtoBanner={content.vtoBanner}
                sspConfig={sspConfig}
                {...featureToggles}
              />
            </VtoConfigProvider>

            {(isFrames || isSunglasses) && <ProductFrameMeasurements product={product} />}

            {isCrossSellEnabled && (
              <CrossSellProducts
                algoliaConfig={crossSellConfig}
                formatProductUrl={formatProductUrl}
              />
            )}

            {isReviewsEnabled && (
              <ReviewDisplay productId={reviewsInitialId} variantId={reviewsInitialSku} />
            )}

            <div className="product-detail-layout__components">
              <CmsComponentSlot path="main/bottom-container" />
            </div>

            <MiniCartModal links={{ cart: routes.cart, checkout: routes.checkout }}>
              {recommendedCategoryKey
                ? (addedLineItems) => (
                    <Upsell
                      recommendedCategoryKey={recommendedCategoryKey}
                      lineItems={addedLineItems}
                    />
                  )
                : null}
            </MiniCartModal>

            <SdProduct
              product={product}
              activeSKU={sku}
              locale={locale}
              staticsUrl={staticsUrl}
              url={productUrl}
            />
          </ReviewsProvider>
        </ProductDetailProvider>
      </DeliveryIndicatorProvider>
    </>
  )
}

ProductDetailPage.getInitialProps = async ({ query, asPath = '' }) => {
  const [, slug] = /.+\/(.+)\/([^?]+)(\?.*)?$/.exec(asPath) ?? []

  if (!slug) {
    console.log(`Can't extract slug and sku from ${asPath}, got slug: '${String(slug)}'`)
    return {
      ...defaultErrorProps,
      statusCode: 404,
    }
  }

  let product: CommerceToolsApi.ProductProjection | undefined
  try {
    product = await ProductsApi.getProductBySlug(endpoints.products, { slug })
  } catch (err: any) {
    // Non OK product service api response, return props to show the error page
    console.error(`Failed to fetch product with slug: ${String(slug)}`, err)
    return {
      ...defaultErrorProps,
      statusCode: err.statusCode,
    }
  }

  // Fetch SSP Products
  let sspExtraProducts: CommerceToolsApi.ProductProjection[] | undefined
  const extrasId = ProductHelpers.getExtrasId(product)
  if (extrasId) {
    const { results } = await ProductsApi.query(endpoints.products, {
      filters: [`productType.id:"${extrasId}"`],
    })
    sspExtraProducts = results
  }

  return {
    sku: product?.masterVariant?.sku ?? '',
    product,
    sspExtraProducts,
    queryParams: query as Record<string, string | string[]>,
  }
}

export default compose(
  withCms(),
  withErrorPage(),
  withLayout(),
  withCart({ readOnly: true })
)(ProductDetailPage)
